﻿import {HTMLInputAutoCompleteAttribute, JSX, SyntheticEvent, useCallback} from "react";
import {useControlId} from "../helpers/useControlId";
import TextArea from "../HtmlControls/TextArea";
import {MvvmTextControlProps} from "./MvvmControlProps";
import {useMvvmFieldSubscription} from "./MvvmDataProvider";
import {useMvvmStringInput} from "./useMvvmStringInput";

import {useRegisterValidation} from "./useRegisterValidation";
import {unwrapMvvmContext} from "./useMvvmTracking";

const identity = (value: string | undefined): string => value ?? "";

export interface MvvmTextAreaProps<T extends object> extends MvvmTextControlProps<T, string> {
	maxLength?: number;
	minLength?: number;
	autoComplete?: HTMLInputAutoCompleteAttribute;
	autoCapitalize?: "off" | "none" | "on" | "sentences" | "words" | "characters" | undefined | string & {};

	rows?: number;
	cols?: number;
	wrap?: "hard" | "soft" | "off" | undefined;
	spellCheck?: boolean;

	validateOnChange?: boolean;
};

export default function MvvmTextArea<T extends object = never>(props: MvvmTextAreaProps<T>): JSX.Element {
	const {
		context: contextProp,
		property,
		id,
		label,
		hint,
		placeholder,
		column,
		controlPrefix,
		controlSuffix,
		hidden,
		disabled,
		inlineError,
		validation,
		transform,
		onChange,
		onBlur,

		validateOnChange,
		maxLength,
		minLength,
		autoFocus,
		autoComplete,
		autoCapitalize,
		rows,
		cols,
		wrap,
		spellCheck
	} = props;

	// Controls read the raw context; see unwrapMvvmContext.
	const context = unwrapMvvmContext(contextProp);
	const controlId = useControlId(id);
	const {value, handleChange, handleBlur} = useMvvmStringInput<T, string>(
		context, property, identity, identity, validateOnChange ?? false, onChange, onBlur, transform
	);

	const onChangeInternal = useCallback(function (e: SyntheticEvent<HTMLTextAreaElement>): void {
		handleChange(e.currentTarget.value);
	}, [handleChange]);

	useRegisterValidation(context, property, controlId, hidden, validation);
	useMvvmFieldSubscription(context, property);

	return <TextArea label={label}
	                 name={property}
	                 value={value}
	                 controlSuffix={controlSuffix}
	                 controlPrefix={controlPrefix}
	                 hint={hint}
	                 hidden={hidden}
	                 column={column}
	                 id={id}
	                 required={validation?.required != null}
	                 error={context.getError(property)?.error}
	                 inlineError={inlineError}
	                 disabled={disabled}
	                 placeholder={placeholder}
	                 maxLength={maxLength}
	                 minLength={minLength}
	                 autoFocus={autoFocus}
	                 autoCapitalize={autoCapitalize}
	                 autoComplete={autoComplete}
	                 onChange={onChangeInternal}
	                 onBlur={handleBlur}
	                 rows={rows}
	                 cols={cols}
	                 wrap={wrap}
	                 spellCheck={spellCheck}
	/>;
};
